import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  Button,
  Input,
  Space,
  Spin,
  Table,
  Typography,
  Upload,
} from "antd";
import {
  DeleteOutlined,
  FileSearchOutlined,
  HolderOutlined,
  PlusOutlined,
  SaveOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  getOcppConfigurationQueryKey,
  getOcppActiveSocketsForSiteQueryKey,
  updateSocketToAssetMapMutation,
} from "../../../services/client/@tanstack/react-query.gen";
import type { OcppConfiguration } from "../../../services/client/types.gen";
import { useNotify } from "../../util-components/Notifications/notifications";
import { useSuggestedMappings } from "./useSuggestedMappings";
import { computeDiff, parseSocketMappingCsv } from "./socketToAssetMapUtils";
import {
  AssetTag,
  AssetTagHandle,
  AssetTagLabel,
  DraggableAssetTag,
  DroppableAssetCell,
  SortableAssetTag,
} from "./AssetTag";
import { useSocketDragHandlers } from "./useSocketDragHandlers";
import { SuggestedMappingsSection } from "./SuggestedMappingsSection";
import { SaveConfirmationModal } from "./SaveConfirmationModal";
import { SocketDetailsDrawer } from "./SocketDetailsDrawer";
import { useSocketDetails } from "./useSocketDetails";
import { DisableForViewer } from "../../common/DisableForViewer";
import { useIsViewer } from "../../../hooks/useIsViewer";

type SocketToAssetMapProps = {
  site_id: string;
  ocpp_configuration: OcppConfiguration;
  charging_pole_names: string[];
};

// --- Main component ---

export const SocketToAssetMap: React.FC<SocketToAssetMapProps> = ({
  site_id,
  ocpp_configuration,
  charging_pole_names,
}) => {
  const notify = useNotify();
  const queryClient = useQueryClient();

  const serverMap = useMemo(
    () => ocpp_configuration.socket_to_asset_map ?? {},
    [ocpp_configuration.socket_to_asset_map],
  );

  // Track the server version to reset draft when it changes
  const serverMapRef = useRef(serverMap);
  const [draftMap, setDraftMap] = useState<Record<string, string[]>>(() =>
    structuredClone(serverMap),
  );
  if (serverMapRef.current !== serverMap) {
    serverMapRef.current = serverMap;
    setDraftMap(structuredClone(serverMap));
  }

  const [customSocketIds, setCustomSocketIds] = useState<string[]>([]);
  const [customInput, setCustomInput] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [csvImportError, setCsvImportError] = useState<{
    fileName: string;
    errors: string[];
  } | null>(null);
  const isViewer = useIsViewer();
  const socketDetails = useSocketDetails();

  const {
    activeSocketIds,
    connectedSocketIds,
    suggestedMappings,
    isLoadingSockets,
    isLoadingSuggestions,
  } = useSuggestedMappings(site_id, draftMap);

  const mutation = useMutation({
    ...updateSocketToAssetMapMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getOcppConfigurationQueryKey({ path: { site_id } }),
      });
      queryClient.invalidateQueries({
        queryKey: getOcppActiveSocketsForSiteQueryKey({ path: { site_id } }),
      });
      notify({
        type: "success",
        message: "Socket-to-Asset Map Updated",
        description: "The mapping has been updated.",
      });
      setConfirmOpen(false);
    },
    onError: () => {
      notify({
        type: "error",
        message: "Failed to update socket-to-asset map",
      });
    },
  });

  // Diff and dirty check
  const diff = useMemo(
    () => computeDiff(serverMap, draftMap),
    [serverMap, draftMap],
  );
  const isDirty = diff.length > 0;

  // All socket IDs: active + custom + those in draftMap. Sorted so rows keep
  // their position across polls instead of following the API's response order.
  const allSocketIds = useMemo(() => {
    const ids = new Set([
      ...activeSocketIds,
      ...customSocketIds,
      ...Object.keys(draftMap),
    ]);
    return Array.from(ids).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );
  }, [activeSocketIds, customSocketIds, draftMap]);

  // Unmatched assets: charging_pole_names not assigned in any draftMap value
  const unmatchedAssets = useMemo(() => {
    const assigned = new Set(Object.values(draftMap).flat());
    return charging_pole_names.filter((name) => !assigned.has(name));
  }, [charging_pole_names, draftMap]);

  // Table data: all sockets, mapped or not
  const tableData = useMemo(
    () =>
      allSocketIds.map((socket_id) => ({
        socket_id,
        asset_ids: draftMap[socket_id] ?? [],
      })),
    [allSocketIds, draftMap],
  );

  // --- Actions ---

  const addAssetToRow = useCallback((socketId: string, assetId: string) => {
    setDraftMap((prev) => {
      const row = prev[socketId] ?? [];
      if (row.includes(assetId)) {
        return prev;
      }
      return { ...prev, [socketId]: [...row, assetId] };
    });
  }, []);

  const removeAssetFromRow = useCallback(
    (socketId: string, assetId: string) => {
      setDraftMap((prev) => {
        const row = prev[socketId];
        if (!row) {
          return prev;
        }
        const next = row.filter((id) => id !== assetId);
        if (next.length === 0) {
          const { [socketId]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [socketId]: next };
      });
    },
    [],
  );

  const removeRow = useCallback((socketId: string) => {
    setDraftMap((prev) => {
      const { [socketId]: _, ...rest } = prev;
      return rest;
    });
    // Also remove from custom sockets if it was manually added
    setCustomSocketIds((prev) => prev.filter((id) => id !== socketId));
  }, []);

  const reorderAssets = useCallback(
    (socketId: string, oldIndex: number, newIndex: number) => {
      setDraftMap((prev) => {
        const row = prev[socketId];
        if (!row) {
          return prev;
        }
        return { ...prev, [socketId]: arrayMove(row, oldIndex, newIndex) };
      });
    },
    [],
  );

  const addCustomSocket = useCallback(() => {
    const trimmed = customInput.trim();
    if (!trimmed) {
      return;
    }
    if (customSocketIds.includes(trimmed) || activeSocketIds.has(trimmed)) {
      return;
    }
    setCustomSocketIds((prev) => [...prev, trimmed]);
    setCustomInput("");
  }, [customInput, customSocketIds, activeSocketIds]);

  // Overwrites the given sockets; sockets not in `mappings` are kept.
  const applyMappings = useCallback((mappings: Record<string, string[]>) => {
    setDraftMap((prev) => {
      const next = { ...prev };
      // Remove these assets from any other socket first
      for (const [socketId, assetIds] of Object.entries(mappings)) {
        for (const assetId of assetIds) {
          for (const [key, values] of Object.entries(next)) {
            if (key in mappings) {
              continue;
            }
            const filtered = values.filter((v) => v !== assetId);
            if (filtered.length === 0) {
              delete next[key];
            } else if (filtered.length !== values.length) {
              next[key] = filtered;
            }
          }
        }
        next[socketId] = assetIds;
      }
      return next;
    });
  }, []);

  const applySuggestion = useCallback(
    (socketId: string, assetIds: string[]) =>
      applyMappings({ [socketId]: assetIds }),
    [applyMappings],
  );

  const importCsv = useCallback(
    async (file: File) => {
      const result = parseSocketMappingCsv(
        await file.text(),
        charging_pole_names,
      );
      if (!result.ok) {
        setCsvImportError({ fileName: file.name, errors: result.errors });
        return;
      }
      setCsvImportError(null);
      applyMappings(result.map);
      const count = Object.keys(result.map).length;
      notify({
        type: "info",
        message: `Loaded ${count} socket${count === 1 ? "" : "s"} from ${file.name}`,
        description: "Review the changes below and click Save to apply them.",
      });
    },
    [applyMappings, charging_pole_names, notify],
  );

  // --- Drag handlers ---

  const {
    activeDrag,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    getVisualConnector,
  } = useSocketDragHandlers(draftMap, addAssetToRow, reorderAssets);

  // --- Save flow ---

  const handleSave = useCallback(() => {
    setConfirmOpen(true);
  }, []);

  const handleConfirmSave = useCallback(() => {
    const socketsToDelete: string[] = [];
    const socketsToUpsert: Record<string, string[]> = {};

    for (const entry of diff) {
      if (entry.kind === "removed") {
        socketsToDelete.push(entry.socketId);
      } else if (entry.kind === "added" || entry.kind === "changed") {
        socketsToUpsert[entry.socketId] =
          entry.kind === "added" ? entry.assets : entry.newAssets;
      }
    }

    mutation.mutate({
      path: { configuration_id: ocpp_configuration._id! },
      body: {
        ...(socketsToDelete.length > 0
          ? { sockets_to_delete: socketsToDelete }
          : {}),
        ...(Object.keys(socketsToUpsert).length > 0
          ? { sockets_to_upsert: socketsToUpsert }
          : {}),
      },
    });
  }, [diff, mutation, ocpp_configuration._id]);

  // --- Table columns ---

  const columns = [
    {
      title: "OCPP Socket ID",
      dataIndex: "socket_id",
      key: "socket_id",
      width: 240,
      render: (socket_id: string) => (
        <Space size="middle">
          {isLoadingSockets ? (
            <Spin size="small" />
          ) : (
            <Badge
              status={connectedSocketIds.has(socket_id) ? "success" : "default"}
            />
          )}
          <Typography.Text strong className="text-sm">
            {socket_id}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: "OCPP Connector IDs (OCPP 1.6) or EVSE IDs (OCPP 2.x)",
      dataIndex: "asset_ids",
      key: "asset_ids",
      render: (asset_ids: string[], record: { socket_id: string }) => (
        <DroppableAssetCell rowKey={record.socket_id}>
          {asset_ids.length === 0 ? (
            <Typography.Text type="secondary" className="text-sm italic">
              No assets assigned — drag from above
            </Typography.Text>
          ) : (
            <SortableContext
              items={asset_ids.map(
                (id) => `sortable-${record.socket_id}-${id}`,
              )}
              strategy={horizontalListSortingStrategy}
            >
              <div className="flex flex-wrap gap-3 items-center">
                {asset_ids.map((id, index) => {
                  const vc = getVisualConnector(record.socket_id, index);
                  return (
                    <SortableAssetTag
                      key={id}
                      id={id}
                      socketId={record.socket_id}
                      connectorIndex={vc.num}
                      highlight={vc.shifted}
                      onRemove={() => removeAssetFromRow(record.socket_id, id)}
                    />
                  );
                })}
              </div>
            </SortableContext>
          )}
        </DroppableAssetCell>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 140,
      render: (_: unknown, record: { socket_id: string }) => {
        // Only show delete for custom sockets or sockets with mappings
        const isActive = activeSocketIds.has(record.socket_id);
        const hasMappings = (draftMap[record.socket_id]?.length ?? 0) > 0;
        return (
          <Space size={4}>
            <Button
              type="text"
              size="small"
              icon={<FileSearchOutlined />}
              onClick={() => socketDetails.open(record.socket_id)}
            >
              Details
            </Button>
            {(!isActive || hasMappings) && (
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => removeRow(record.socket_id)}
              />
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div className="mt-8">
      <DndContext
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <Typography.Title level={4} className="!mb-0">
            Socket-to-Asset Map
          </Typography.Title>
          <Space>
            <Upload
              accept=".csv,text/csv"
              showUploadList={false}
              disabled={isViewer}
              beforeUpload={(file) => {
                void importCsv(file);
                return false;
              }}
            >
              <DisableForViewer>
                <Button icon={<UploadOutlined />} size="large">
                  Import CSV
                </Button>
              </DisableForViewer>
            </Upload>
            <DisableForViewer>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                disabled={!isDirty}
                onClick={handleSave}
                size="large"
              >
                Save
              </Button>
            </DisableForViewer>
          </Space>
        </div>

        <Typography.Paragraph type="secondary" className="text-xs !-mt-4">
          Import CSV expects the columns <code>socket_id</code>,{" "}
          <code>connector_id</code>, <code>asset_id</code>, separated by{" "}
          <code>,</code> or <code>;</code>. Sockets in the file replace their
          current mapping; other sockets are left unchanged.
        </Typography.Paragraph>

        {csvImportError && (
          <Alert
            type="error"
            showIcon
            closable
            onClose={() => setCsvImportError(null)}
            className="mb-6"
            message={`Could not import ${csvImportError.fileName}. Nothing was changed.`}
            description={
              <ul className="m-0 pl-4">
                {csvImportError.errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            }
          />
        )}

        {/* Suggested mappings based on transaction correlations */}
        <SuggestedMappingsSection
          suggestedMappings={suggestedMappings}
          connectedSocketIds={connectedSocketIds}
          isLoading={isLoadingSuggestions}
          isLoadingSockets={isLoadingSockets}
          onApply={applySuggestion}
        />

        {/* Unmatched assets pool */}
        <div className="mb-6 p-4 rounded-lg bg-gray-50 border border-gray-200">
          <Typography.Text strong className="text-sm">
            Unmatched Assets{" "}
            <Typography.Text type="secondary" className="text-xs font-normal">
              (from site configuration)
            </Typography.Text>
          </Typography.Text>
          <div className="mt-3 flex flex-wrap gap-3">
            {unmatchedAssets.length === 0 && (
              <Typography.Text type="secondary" className="text-sm italic">
                All assets are assigned
              </Typography.Text>
            )}
            {unmatchedAssets.map((id) => (
              <DraggableAssetTag key={id} id={id} />
            ))}
          </div>
        </div>

        {/* Mapping table */}
        <Table
          dataSource={tableData}
          columns={columns}
          rowKey="socket_id"
          pagination={false}
          size="small"
          loading={isLoadingSockets && tableData.length === 0}
          locale={{
            emptyText: "No sockets found. Add a custom socket ID below.",
          }}
        />

        {/* Add custom socket */}
        <div className="mt-4">
          <Input.Search
            placeholder="Add custom OCPP socket ID"
            enterButton={
              <span>
                <PlusOutlined /> Add
              </span>
            }
            size="middle"
            className="w-[340px]"
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            onSearch={addCustomSocket}
          />
        </div>

        {/* Drag overlay */}
        <DragOverlay>
          {activeDrag ? (
            activeDrag.type === "sortable-asset" ? (
              <AssetTag className="cursor-grabbing" elevated>
                <AssetTagHandle
                  highlight={activeDrag.overIndex !== activeDrag.originalIndex}
                >
                  #{activeDrag.connectorIndex}
                </AssetTagHandle>
                <AssetTagLabel>{activeDrag.id}</AssetTagLabel>
              </AssetTag>
            ) : (
              <AssetTag className="cursor-grabbing" elevated>
                <AssetTagHandle>
                  <HolderOutlined />
                </AssetTagHandle>
                <AssetTagLabel>{activeDrag.id}</AssetTagLabel>
              </AssetTag>
            )
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Save confirmation modal */}
      <SaveConfirmationModal
        open={confirmOpen}
        onOk={handleConfirmSave}
        onCancel={() => setConfirmOpen(false)}
        confirmLoading={mutation.isPending}
        diff={diff}
      />

      <SocketDetailsDrawer
        site_id={site_id}
        socket_id={socketDetails.socketId}
        connected={
          socketDetails.socketId !== null &&
          connectedSocketIds.has(socketDetails.socketId)
        }
        onClose={socketDetails.close}
      />
    </div>
  );
};
